import { University } from "@/store/types/universityTypes";
import { ImageBanner } from "../common/ImageBanner";
import { ImageBackground } from "../common/ImageBackground";
import { MapPin } from "lucide-react";
import Link from "next/link";
import { sendCustomGaEvent } from "@/scripts/ga";
import { useAuth } from "@/hook/useAuth";
import { useEffect, useState } from "react";
import TalkToExperts from "../homepage/TalkToExperts";

const NewBanner = ({ universityDetail }: { universityDetail: University }) => {
  const { isAnonymous } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [expertForm, setExpertForm] = useState(false);
  const addressInfo = universityDetail?.addressInfo;

  const expertFromHandler = () => {
    void sendCustomGaEvent("university_talk_to_experts_open_click", {
      is_anonymous: isAnonymous,
    });
    setExpertForm(true);
  };

  useEffect(() => {
    setCurrentStep(isAnonymous ? 1 : 2);
  }, [expertForm, isAnonymous]);

  return (
    <>
      <div className="min-h-[calc(100vh-67px)] relative flex items-end sm:items-center pb-[32px] sm:pb-0">
        <span className="w-[20%] h-[20%] bg-[#8F3EBC2E] rounded-full absolute top-1/2 left-[30%] blur-[100px] z-20"></span>
        <span className="z-10 absolute top-0 left-0 w-full h-full bg-[linear-gradient(180deg,_rgba(0,_0,_0,_0.6)_0%,_rgba(102,_102,_102,_0)_100%),linear-gradient(359.22deg,_#021935_13.44%,_rgba(60,_70,_82,_0.415298)_53.13%,_rgba(102,_102,_102,_0)_77.98%)] md:bg-[linear-gradient(180deg,_rgba(0,_0,_0,_0.6)_0%,_rgba(102,_102,_102,_0)_100%),linear-gradient(270deg,_rgba(102,_102,_102,_0)_-24.58%,_#021935_75.38%)]"></span>
        <span className="z-0 absolute top-0 left-0 w-full h-full">
          <ImageBackground
            college_name={universityDetail?.name || ""}
            country={addressInfo?.country || ""}
          />
        </span>
        <div className="container w-full px-4 sm:px-6 md:px-8 2xl:max-w-[1581px] relative z-30 h-full pt-[60px]">
          <span className="border-[1.5px] border-white bg-[#213047] rounded-full py-2 px-4 inline-flex items-center justify-center gap-2">
            <p className="text-xs sm:text-sm font-medium ff_poppins text-white capitalize">
              {universityDetail?.category} University
            </p>
            <span className="size-1 bg-[#D9D9D9] rounded-full"></span>
            <p className="text-xs sm:text-sm font-medium ff_poppins text-white capitalize">
              {universityDetail?.addressInfo?.country}
            </p>
          </span>
          <h1 className="max-w-[700px] text-[28px] sm:text-3xl md:text-4xl lg:text-[44px] xl:text-[48px] font-medium ff_poppins md:leading-[64px] text-white mt-4">
            {universityDetail?.name || "University"}
          </h1>
          <div className="mt-3 sm:mt-7 md:mt-9 lg:mt-11 xl:mt-12 flex items-center gap-3">
            {universityDetail?.addressInfo?.city &&
              universityDetail?.addressInfo?.state && (
                <div className="flex items-center gap-2">
                  <MapPin className="text-white size-[16px] sm:size-[20px] md:size-[24px]" />
                  <p className="text-xs sm:text-sm md:text-base xl:text-lg font-medium ff_poppins text-white">
                    {universityDetail?.addressInfo?.city},{" "}
                    {universityDetail?.addressInfo?.state}
                  </p>
                </div>
              )}
            <span className="size-2 bg-[#D9D9D9] rounded-full"></span>
            {universityDetail?.foundedYear && (
              <p className="text-xs sm:text-sm md:text-base xl:text-lg font-medium ff_poppins text-white">
                Estd. {universityDetail?.foundedYear}
              </p>
            )}
            {/* <span className="size-2 bg-[#D9D9D9] rounded-full"></span> */}
          </div>
          <div className="mt-[28px] flex flex-wrap items-center gap-y-4 max-w-[900px] me-auto">
            {universityDetail.metaData?.international_students_percentage && (
              <div className="border-r border-r-[#818181] pr-[18px] md:pr-5 xl:pr-6">
                <p className="font-medium ff_poppins text-[#9a9a9a] text-xs sm:text-sm mb-1">
                  Intl. Students
                </p>
                <p className="text-base md:text-lg lg:text-xl xl:text-2xl text-white font-medium ff_poppins">
                  {universityDetail.metaData?.international_students_percentage}
                </p>
              </div>
            )}
            {(universityDetail as any).rankDetails?.['QS Ranking'] &&
              (universityDetail as any).rankDetails?.['QS Ranking Year'] && (
              <div className="pl-[18px] md:pl-5 xl:pl-6 border-r border-r-[#818181] pr-[18px] md:pr-5 xl:pr-6">
                <p className="font-medium ff_poppins text-[#9a9a9a] text-xs sm:text-sm mb-1">
                  QS Ranking
                </p>
                <p className="text-base md:text-lg lg:text-xl xl:text-2xl text-white font-medium ff_poppins">
                  {(universityDetail as any).rankDetails['QS Ranking']}
                </p>
              </div>
            )}
            {universityDetail.programCount && (
              <div className={`pl-[18px] md:pl-5 xl:pl-6 ${(universityDetail.metaData?.application_acceptance_rate_pg || universityDetail.metaData?.application_acceptance_rate_ug) ? 'border-r border-r-[#818181] pr-[18px] md:pr-5 xl:pr-6' : ''}`}>
                <p className="font-medium ff_poppins text-[#9a9a9a] text-xs sm:text-sm mb-1">
                  Programs
                </p>
                <p className="text-base md:text-lg lg:text-xl xl:text-2xl text-white font-medium ff_poppins">
                  {universityDetail.programCount}
                </p>
              </div>
            )}
            {(universityDetail.metaData?.application_acceptance_rate_pg ||
              universityDetail.metaData?.application_acceptance_rate_ug) && (
              <div className="pl-[18px] md:pl-5 xl:pl-6">
                <p className="font-medium ff_poppins text-[#9a9a9a] text-xs sm:text-sm mb-1">
                  Acceptance Rate
                </p>
                <p className="text-base md:text-lg lg:text-xl xl:text-2xl text-white font-medium ff_poppins whitespace-nowrap">
                  {(() => {
                    const pgRate = universityDetail.metaData?.application_acceptance_rate_pg 
                      ? `${universityDetail.metaData.application_acceptance_rate_pg.replace('%', '')}% (PG)` 
                      : null;
                    const ugRate = universityDetail.metaData?.application_acceptance_rate_ug 
                      ? `${universityDetail.metaData.application_acceptance_rate_ug.replace('%', '')}% (UG)` 
                      : null;
                    
                    if (pgRate && ugRate) {
                      return `${ugRate} / ${pgRate}`;
                    }
                    return ugRate || pgRate;
                  })()}
                </p>
              </div>
            )}
          </div>

          {/* <div className="mt-[28px] max-w-[519px] grid grid-cols-3">
          <div className="border-r border-[#818181]">
          <p></p>
          </div>
        </div> */}

          {/* NAVIGATE BUTTONS */}
          <div className="flex items-center gap-[15px] mt-[18px] sm:mt-[24px] md:mt-[32px] lg:mt-[40px] xl:mt-[56px]">
            <Link
              href={`/all-courses/${universityDetail?.college_slug}`}
              className="bg-primary-gradient text-white font-semibold text-sm sm:text-base md:text-lg ff_poppins rounded-full py-2 sm:py-[10px] md:py-[12px] px-[14px] sm:px-[24px] md:px-[28px] lg:px-[30px]"
            >
              View Programs
            </Link>
            <button
              onClick={expertFromHandler}
              className="border-[1.5px] border-white rounded-full text-sm sm:text-base md:text-lg py-2 sm:py-[10px] md:py-[12px] px-[14px] sm:px-[24px] md:px-[28px] lg:px-[30px] text-white font-semibold ff_poppins bg-[#2A293F]"
            >
              Talk to Mentor
            </button>
          </div>
        </div>
      </div>
      {expertForm && (
        <TalkToExperts
          expertForm={expertForm}
          setExpertForm={setExpertForm}
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
        />
      )}
    </>
  );
};
export default NewBanner;
